import React, { useEffect, useRef } from "react";
import { Tooltip, IconButton, Paper, Popper } from "@mui/material";
import QrCode2Icon from '@mui/icons-material/QrCode2';
import { MeetingInfoContext } from "../../../../context";
import JoinMeetingQR from "./join-meeting-qr";

interface QRCodeToggleButtonProps {
  /** Whether the QR code panel is currently open */
  isOpen: boolean;
  /** Callback function to toggle the QR code panel */
  onToggle: () => void;
}

/**
 * QRCodeToggleButton - A button for toggling the QR code panel visibility
 * 
 * @param isOpen - Current state of the QR code panel
 * @param onToggle - Function to call when the button is clicked
 */
const QRCodeToggleButton: React.FC<QRCodeToggleButtonProps> = ({
  isOpen,
  onToggle
}) => {
  const { meetingId, userId, joinCode } = React.useContext(MeetingInfoContext);
  
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setAnchorEl(buttonRef.current);
  }, []);

  const disabled = joinCode.length === 0;
  const open = Boolean(anchorEl) && isOpen && !disabled;
  const id = open ? 'qr-popover' : undefined;

  return (
    <>
      <Tooltip title={isOpen ? "Hide QR Code" : "Show QR Code"}>
        <IconButton
          ref={buttonRef}
          disabled={disabled}
          onClick={onToggle}
          sx={{ color: isOpen ? 'primary.main' : '#666' }}
        >
          <QrCode2Icon />
        </IconButton>
      </Tooltip>

      <Popper
        open={open}
        anchorEl={anchorEl}
        id={id}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Paper
          onClick={onToggle}
          sx={{
            padding: 1,
            pb: 0.5,
            backgroundColor: "white",
            cursor: "pointer",
          }}
        >
          <JoinMeetingQR
            meetingId={meetingId}
            userId={userId}
            joinCode={joinCode}
            qrCodeSize={Math.min(window.innerHeight - 100, 400)}
          />
        </Paper>
      </Popper>
    </>
  );
};

export default QRCodeToggleButton;